/**
 * ModernAssetsScreen Component
 * Modern GPU Store and My Assets interface with mining simulation
 */

import { useState, useEffect, useRef } from 'react';
import { useUserContext } from '../contexts';
import { useTranslation } from '../hooks/useTranslation';
import { PurchaseCurrencyModal } from '../components/PurchaseCurrencyModal';
import { api } from '../services/api';

type TabType = 'store' | 'portfolio';

interface MinerAsset {
  id: string;
  name: string;
  image: string;
  dailyIncome: number;
  priceUSDT: number;
  priceWiTon: number;
  priceStars: number;
  description: string;
  gradient: string;
}

const MINERS: MinerAsset[] = [
  { id: 'basic-miner', name: 'Basic Miner', image: '/assets/miners/basic-miner.png', dailyIncome: 83, priceUSDT: 10, priceWiTon: 50000, priceStars: 650, description: 'Entry-level mining card', gradient: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)' },
  { id: 'rx580', name: 'RX 580 Miner', image: '/assets/miners/rx580.jpg', dailyIncome: 291, priceUSDT: 25, priceWiTon: 125000, priceStars: 1650, description: 'Reliable AMD mining card', gradient: 'linear-gradient(135deg, #4c1d95 0%, #7c3aed 100%)' },
  { id: 'gtx1660', name: 'GTX 1660 Miner', image: '/assets/miners/gtx1660.jpg', dailyIncome: 833, priceUSDT: 50, priceWiTon: 250000, priceStars: 3250, description: 'Efficient NVIDIA card', gradient: 'linear-gradient(135deg, #065f46 0%, #10b981 100%)' },
  { id: 'rtx3060', name: 'RTX 3060 Miner', image: '/assets/miners/rtx3060.jpg', dailyIncome: 2000, priceUSDT: 100, priceWiTon: 500000, priceStars: 6500, description: 'High-performance mining', gradient: 'linear-gradient(135deg, #0c4a6e 0%, #0ea5e9 100%)' },
  { id: 'rtx3070', name: 'RTX 3070 Miner', image: '/assets/miners/rtx3070.jpg', dailyIncome: 5833, priceUSDT: 250, priceWiTon: 1250000, priceStars: 16500, description: 'Premium mining power', gradient: 'linear-gradient(135deg, #7c2d12 0%, #ea580c 100%)' },
  { id: 'rtx3080', name: 'RTX 3080 Miner', image: '/assets/miners/rtx3080.jpg', dailyIncome: 12500, priceUSDT: 500, priceWiTon: 2500000, priceStars: 32500, description: 'Top-tier mining beast', gradient: 'linear-gradient(135deg, #991b1b 0%, #dc2626 100%)' },
  { id: 'ultimate-farm', name: 'Ultimate Farm', image: '/assets/miners/ultimate-farm.jpg', dailyIncome: 26666, priceUSDT: 1000, priceWiTon: 5000000, priceStars: 65000, description: 'Complete mining operation', gradient: 'linear-gradient(135deg, #713f12 0%, #f59e0b 100%)' },
  { id: 'profit-king-miner', name: 'Profit King Miner', image: '/assets/miners/Profit king miner.png', dailyIncome: 70833, priceUSDT: 2500, priceWiTon: 12500000, priceStars: 162500, description: 'PREMIUM - King of profits', gradient: 'linear-gradient(135deg, #581c87 0%, #a855f7 100%)' },
  { id: 'legendary-generator', name: 'Legendary Generator', image: '/assets/miners/Legendary Generator.png', dailyIncome: 150000, priceUSDT: 5000, priceWiTon: 25000000, priceStars: 325000, description: 'PREMIUM - Legendary power', gradient: 'linear-gradient(135deg, #be123c 0%, #f43f5e 100%)' },
  { id: 'big-ultimate-farm', name: 'Big Ultimate Farm', image: '/assets/miners/Big Ultimate Farm.png', dailyIncome: 333333, priceUSDT: 10000, priceWiTon: 50000000, priceStars: 650000, description: 'PREMIUM - Ultimate empire', gradient: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)' },
];

const SOLD_OUT_IDS = ['basic-miner', 'rx580', 'gtx1660']; // $10, $25, $50 packages

export function ModernAssetsScreen() {
  const { user, refreshUser } = useUserContext();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<TabType>('store');
  const [purchasing, setPurchasing] = useState<string | null>(null);
  const [selectedAsset, setSelectedAsset] = useState<MinerAsset | null>(null);
  const [isPurchaseModalOpen, setIsPurchaseModalOpen] = useState(false);
  const [portfolio, setPortfolio] = useState<any>(null);
  const [loadingPortfolio, setLoadingPortfolio] = useState(false);
  
  // Mining simulation state
  const [miningStatus, setMiningStatus] = useState<any>(null);
  const [displayedMined, setDisplayedMined] = useState(0);
  const [claiming, setClaiming] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (activeTab === 'portfolio') {
      loadPortfolio();
      loadMiningStatus();
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [activeTab]);

  // Mining counter animation - increment every second
  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (miningStatus && miningStatus.isActive && !miningStatus.isStopped && miningStatus.perMinute > 0) {
      setDisplayedMined(miningStatus.accumulated);
      timerRef.current = setInterval(() => {
        setDisplayedMined(prev => prev + (miningStatus.perMinute / 60));
      }, 1000);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [miningStatus]);

  const loadPortfolio = async () => {
    setLoadingPortfolio(true);
    try {
      const response = await api.getPortfolio();
      setPortfolio(response.data);
    } catch (error) {
      console.error('Failed to load portfolio:', error);
    } finally {
      setLoadingPortfolio(false);
    }
  };

  const loadMiningStatus = async () => {
    try {
      const res = await api.getMiningStatus();
      setMiningStatus(res.data);
      setDisplayedMined(res.data.accumulated || 0);
    } catch (error) {
      console.error('Failed to load mining status:', error);
    }
  };

  const handleClaimMining = async () => {
    setClaiming(true);
    try {
      const res = await api.claimMining();
      alert(`✅ Получено ${res.data.claimed.toLocaleString()} WiTon!`);
      await refreshUser();
      await loadMiningStatus();
    } catch (error: any) {
      alert(error.response?.data?.error?.message || 'Ошибка получения');
    } finally {
      setClaiming(false);
    }
  };

  const handleOpenPurchaseModal = (asset: MinerAsset) => {
    setSelectedAsset(asset);
    setIsPurchaseModalOpen(true);
  };

  const handlePurchase = async (currency: 'usdt' | 'stars') => {
    if (!user || !selectedAsset) { alert('User or asset not found'); return; }
    setPurchasing(selectedAsset.id);
    setIsPurchaseModalOpen(false);
    try {
      const storeResponse = await api.getAssetStore();
      const dbAsset = storeResponse.data.find((a: any) => a.name === selectedAsset.name);
      if (!dbAsset) throw new Error('Asset not found in store');

      if (currency === 'stars') {
        const invoiceResponse = await api.createStarsInvoice({ type: 'asset', itemId: dbAsset.id, starsAmount: selectedAsset.priceStars });
        const invoiceLink = invoiceResponse.data.invoiceLink;
        if (window.Telegram?.WebApp?.openInvoice) {
          window.Telegram.WebApp.openInvoice(invoiceLink, (status: string) => {
            if (status === 'paid') { refreshUser(); loadPortfolio(); setActiveTab('portfolio'); alert(`Successfully purchased ${selectedAsset.name}!`); }
            else if (status === 'failed' || status === 'cancelled') { alert('Payment was cancelled or failed'); }
            setPurchasing(null);
          });
        } else { window.open(invoiceLink, '_blank'); setPurchasing(null); }
      } else if (currency === 'usdt') {
        await api.purchaseAsset(dbAsset.id, 'usdt');
        await refreshUser(); await loadPortfolio(); setActiveTab('portfolio');
        alert(`Successfully purchased ${selectedAsset.name}!`);
        setPurchasing(null);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error?.message || error.message || 'Purchase failed';
      alert(errorMessage);
      setPurchasing(null);
    }
  };

  return (
    <div style={{ minHeight: '100vh', maxHeight: '100vh', background: 'linear-gradient(180deg, #0a1628 0%, #000814 100%)', color: '#fff', paddingBottom: '80px', overflowY: 'auto', overflowX: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '20px', textAlign: 'center', background: 'rgba(10, 22, 40, 0.8)', backdropFilter: 'blur(10px)' }}>
        <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>Market</h1>
      </div>

      {/* Tab Switcher */}
      <div style={{ display: 'flex', padding: '12px 16px', gap: '12px' }}>
        <button onClick={() => setActiveTab('store')} style={{
          flex: 1, padding: '12px',
          background: activeTab === 'store' ? 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)' : 'rgba(30, 58, 138, 0.3)',
          border: activeTab === 'store' ? '1px solid #3b82f6' : '1px solid rgba(59, 130, 246, 0.3)',
          borderRadius: '12px', color: '#fff', fontSize: '16px', fontWeight: activeTab === 'store' ? 600 : 400, cursor: 'pointer',
        }}>GPU Store</button>
        <button onClick={() => setActiveTab('portfolio')} style={{
          flex: 1, padding: '12px',
          background: activeTab === 'portfolio' ? 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)' : 'rgba(30, 58, 138, 0.3)',
          border: activeTab === 'portfolio' ? '1px solid #3b82f6' : '1px solid rgba(59, 130, 246, 0.3)',
          borderRadius: '12px', color: '#fff', fontSize: '16px', fontWeight: activeTab === 'portfolio' ? 600 : 400, cursor: 'pointer',
        }}>My Assets</button>
      </div>

      {/* GPU Store Tab */}
      {activeTab === 'store' && (
        <div style={{ padding: '0 16px' }}>
          {MINERS.map((miner) => {
            const isSoldOut = SOLD_OUT_IDS.includes(miner.id);
            return (
            <div key={miner.id} style={{ background: 'rgba(10, 22, 40, 0.6)', borderRadius: '16px', padding: '16px', marginBottom: '12px', border: '1px solid rgba(59, 130, 246, 0.2)', display: 'flex', alignItems: 'center', gap: '16px', opacity: isSoldOut ? 0.6 : 1 }}>
              <div style={{ width: '80px', height: '80px', background: miner.gradient, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden' }}>
                <img src={miner.image} alt={miner.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.parentElement!.innerHTML = '<span style="font-size: 40px;">🎮</span>'; }} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '18px', fontWeight: 600, marginBottom: '4px' }}>{miner.name}</div>
                <div style={{ fontSize: '14px', color: isSoldOut ? '#6b7280' : '#10b981', fontWeight: 600, marginBottom: '8px' }}>+{miner.dailyIncome.toLocaleString()} WiTon/24h</div>
                <div style={{ fontSize: '16px', fontWeight: 700, color: isSoldOut ? '#6b7280' : '#fbbf24', lineHeight: '1.3' }}>{miner.priceUSDT} USDT</div>
                <div style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.6)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '120px' }}>
                  ≈ {miner.priceWiTon >= 1000000 ? `${(miner.priceWiTon / 1000000).toFixed(1)}M` : miner.priceWiTon.toLocaleString()} WiTon
                </div>
              </div>
              {isSoldOut ? (
                <div style={{
                  padding: '12px 20px',
                  background: 'rgba(107, 114, 128, 0.3)',
                  border: '1px solid rgba(107, 114, 128, 0.4)',
                  borderRadius: '12px',
                  color: '#9ca3af',
                  fontSize: '14px',
                  fontWeight: 700,
                  textAlign: 'center',
                }}>Sold Out</div>
              ) : (
              <button onClick={() => handleOpenPurchaseModal(miner)} disabled={purchasing === miner.id || !user} style={{
                padding: '12px 24px',
                background: purchasing === miner.id || !user ? 'rgba(59, 130, 246, 0.3)' : 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
                border: 'none', borderRadius: '12px', color: '#fff', fontSize: '16px', fontWeight: 600,
                cursor: purchasing === miner.id || !user ? 'not-allowed' : 'pointer',
                boxShadow: purchasing === miner.id || !user ? 'none' : '0 4px 12px rgba(59, 130, 246, 0.4)',
              }}>{purchasing === miner.id ? '...' : 'Buy'}</button>
              )}
            </div>
            );
          })}
        </div>
      )}

      {/* My Assets Tab with Mining Simulation */}
      {activeTab === 'portfolio' && (
        <div style={{ padding: '0 16px' }}>
          {loadingPortfolio ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: 'rgba(255, 255, 255, 0.6)' }}>
              <div style={{ fontSize: '18px' }}>Loading...</div>
            </div>
          ) : portfolio && portfolio.assets && portfolio.assets.length > 0 ? (
            <>
              {/* Mining Simulation Card */}
              {miningStatus && miningStatus.isActive && (
                <div style={{
                  background: 'linear-gradient(135deg, #065f46 0%, #10b981 100%)',
                  borderRadius: '16px', padding: '20px', marginBottom: '16px',
                  border: '1px solid rgba(16, 185, 129, 0.3)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                    <span style={{ fontSize: '32px' }}>⛏️</span>
                    <div>
                      <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.8)' }}>{t('home.mining_active')}</div>
                      <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>
                        +{miningStatus.perMinute.toFixed(2)} {t('home.mining_per_min')}
                      </div>
                    </div>
                  </div>
                  
                  {/* Counter */}
                  <div style={{
                    background: 'rgba(0,0,0,0.3)', borderRadius: '12px', padding: '16px',
                    textAlign: 'center', marginBottom: '12px',
                  }}>
                    <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)', marginBottom: '4px' }}>
                      {t('home.mining_mined')}
                    </div>
                    <div style={{ fontSize: '32px', fontWeight: 700, color: '#fbbf24', fontFamily: 'monospace' }}>
                      {Math.floor(displayedMined).toLocaleString()} WiTon
                    </div>
                    {miningStatus.isStopped && (
                      <div style={{ fontSize: '12px', color: '#ef4444', marginTop: '4px' }}>
                        ⚠️ {t('home.mining_stopped')}
                      </div>
                    )}
                  </div>

                  {/* Claim Button */}
                  <button
                    onClick={handleClaimMining}
                    disabled={claiming || Math.floor(displayedMined) <= 0}
                    style={{
                      width: '100%', padding: '14px',
                      background: claiming || Math.floor(displayedMined) <= 0
                        ? 'rgba(255,255,255,0.2)'
                        : 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)',
                      border: 'none', borderRadius: '12px', color: '#fff',
                      fontSize: '16px', fontWeight: 700,
                      cursor: claiming || Math.floor(displayedMined) <= 0 ? 'not-allowed' : 'pointer',
                      boxShadow: claiming ? 'none' : '0 4px 12px rgba(245, 158, 11, 0.4)',
                    }}
                  >
                    {claiming ? t('home.mining_claiming') : t('home.mining_claim', { amount: Math.floor(displayedMined).toLocaleString() })}
                  </button>
                </div>
              )}

              {/* Summary Card */}
              <div style={{
                background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
                borderRadius: '16px', padding: '20px', marginBottom: '16px',
                border: '1px solid rgba(59, 130, 246, 0.3)',
              }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                  <div>
                    <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.8)', marginBottom: '4px' }}>Total Assets</div>
                    <div style={{ fontSize: '24px', fontWeight: 700 }}>{portfolio.assets.length}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.8)', marginBottom: '4px' }}>Daily Income</div>
                    <div style={{ fontSize: '24px', fontWeight: 700, color: '#10b981' }}>+{portfolio.totalDailyIncome.toFixed(0)}</div>
                  </div>
                </div>
              </div>

              {/* Assets List */}
              {portfolio.assets.map((userAsset: any) => {
                const miner = MINERS.find(m => m.name === userAsset.asset.name);
                return (
                  <div key={userAsset.id} style={{ background: 'rgba(10, 22, 40, 0.6)', borderRadius: '16px', padding: '16px', marginBottom: '12px', border: '1px solid rgba(59, 130, 246, 0.2)', display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div style={{ width: '80px', height: '80px', background: miner?.gradient || 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden' }}>
                      {miner ? <img src={miner.image} alt={userAsset.asset.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.parentElement!.innerHTML = '<span style="font-size: 40px;">🎮</span>'; }} /> : <span style={{ fontSize: '40px' }}>🎮</span>}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '18px', fontWeight: 600, marginBottom: '4px' }}>{userAsset.asset.name}</div>
                      <div style={{ fontSize: '14px', color: '#10b981', fontWeight: 600, marginBottom: '4px' }}>+{(userAsset.asset.monthlyProfit / 30).toFixed(0)} WiTon/24h</div>
                      <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)' }}>Purchased: {new Date(userAsset.purchasedAt).toLocaleDateString()}</div>
                    </div>
                    <div style={{ padding: '8px 16px', background: 'rgba(16, 185, 129, 0.2)', border: '1px solid rgba(16, 185, 129, 0.3)', borderRadius: '20px', color: '#10b981', fontSize: '14px', fontWeight: 600 }}>Active</div>
                  </div>
                );
              })}
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: 'rgba(255, 255, 255, 0.6)' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>📦</div>
              <div style={{ fontSize: '18px', marginBottom: '8px' }}>No Assets Yet</div>
              <div style={{ fontSize: '14px', marginBottom: '20px' }}>Purchase miners from the store to start earning passive income</div>
              <button onClick={() => setActiveTab('store')} style={{ padding: '12px 24px', background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)', border: 'none', borderRadius: '12px', color: '#fff', fontSize: '16px', fontWeight: 600, cursor: 'pointer' }}>Browse Store</button>
            </div>
          )}
        </div>
      )}

      {selectedAsset && (
        <PurchaseCurrencyModal isOpen={isPurchaseModalOpen} onClose={() => setIsPurchaseModalOpen(false)} assetName={selectedAsset.name} priceUsdt={selectedAsset.priceUSDT} priceStars={selectedAsset.priceStars} usdtBalance={user?.usdtBalance || 0} onPurchase={handlePurchase} allowStars={false} />
      )}
    </div>
  );
}
